import { camelizeKeys, decamelizeKeys } from 'humps';
import { omit } from 'lodash';
import { purchase as purchaseApi } from 'js-api-client';
import store from '@/store';
import {
  fetchTripsDetails,
  getPurchase,
  selectSeats,
  setPassengers,
  lockTickets,
  lockTicketsLegacy,
  documentWithoutSize,
  toggleInsurance,
} from '@/actions/purchase';
import { setError } from '@/actions';
import { decodePassengers } from 'utils/Reserbus';
import { getQuickPurchasePassengers } from 'utils/quickPurchasePassengers';
import { getQuickPurchaseInsuranceSelection } from 'utils/quickPurchaseInsurance';
import autoSelectSeats, { findSeatsInLayout } from 'utils/purchase/autoSelectSeats';
import { getBrand2ProcessorMap } from 'utils/documentMaps';
import normalizeRegex from 'utils/normalizeRegex';
import { formatName } from 'utils/purchase/parsePassenger';
import { setUserTokenForAPI, getWalletTypeForPurchase } from 'utils/loyalty';
import errorMonitoring from 'errorMonitoring/ErrorMonitoring';

/**
 * Returns the backend document type for a quick-purchase passenger.
 * @param {object} passenger - Passenger data.
 * @returns {string} Processor document type.
 */
function getPassengerDocumentType(passenger) {
  const brand2ProcessorMap = getBrand2ProcessorMap();
  return brand2ProcessorMap[passenger.documentType] || passenger.documentType;
}

/**
 * Serializes a quick-purchase passenger for the purchase API.
 * @param {object} params - Serialization params.
 * @param {object} params.passenger - Passenger data.
 * @param {object} params.features - Tenant feature flags.
 * @param {number} [params.purchaserNationalityId] - Purchaser nationality id.
 * @param {object} [params.insuranceSelection] - Passenger insurance flags.
 * @returns {object} API-ready passenger data.
 */
function serializeQuickPurchasePassenger({
  passenger,
  features,
  purchaserNationalityId,
  insuranceSelection = {},
}) {
  return {
    ...formatName(passenger),
    ...insuranceSelection,
    ...(features.DOCUMENT_PER_PASSENGER &&
      passenger?.document && {
        document: documentWithoutSize(passenger.document),
      }),
    ...(purchaserNationalityId && {
      nationalityId: purchaserNationalityId,
    }),
    document_type: getPassengerDocumentType(passenger),
    isoCountryCode: passenger.nationality || passenger.isoCountryCode,
    phone: normalizeRegex('[^0-9]', passenger.phone),
  };
}

/**
 * savePassengersForQuickPurchase.
 * Save passengers and lock tickets for the quick purchase flow.
 * Unlike savePassengersWithTickets, this properly awaits lockTickets to prevent
 * race conditions when the QuickCheckout UI becomes interactive.
 * @param {Function} dispatch - Redux dispatch function.
 * @param {string} token - Purchase token.
 * @param {object} fields - Passenger and purchaser fields.
 * @param {object} trips - Trips object with departs and optional returns.
 */
async function savePassengersForQuickPurchase(dispatch, token, fields, trips) {
  const { env, features } = store.getState().whitelabelConfig;
  const { availableWallets = [] } = store.getState().purchase.toJS();

  const purchasePayloadBase =
    env.brand === 'gfa'
      ? decamelizeKeys({
          purchaserNationalityId: 1,
          ...omit(fields, 'passengers'),
          email: fields.email,
        })
      : decamelizeKeys({
          purchaserNationalityId: 1,
          omitEmailValidation: true,
          ...omit(fields, 'passengers'),
          email: features.NEEDS_PASSENGER_EMAIL_ON_PURCHASER_PAYLOAD
            ? fields.passengers[0].email
            : fields.email,
        });

  const passengersPayload = decamelizeKeys(
    fields.passengers.map((passenger) => {
      return serializeQuickPurchasePassenger({
        passenger,
        features,
        purchaserNationalityId: purchasePayloadBase.purchaser_nationality_id,
      });
    }),
  );

  setUserTokenForAPI();
  const walletType = getWalletTypeForPurchase();
  const isValidWalletType = walletType && availableWallets?.includes(walletType);

  const purchasePayload = {
    ...purchasePayloadBase,
    wallet_type: isValidWalletType ? walletType : null,
    used_frequent_passengers: fields.passengers.some((p) => p.isFrequentPassenger),
    used_autoselected_passenger: fields.passengers.some((p) => p.preferredPassengerOrder),
  };

  let purchaseResponse;
  try {
    purchaseResponse = await purchaseApi.update(token, purchasePayload);
  } catch (error) {
    errorMonitoring.notify({
      error,
      info: { from: 'quickPurchase:updatePurchaser', token },
    });
    throw error;
  }

  let payload;
  try {
    if (purchaseResponse.purchase.passengers.length) {
      payload = await purchaseApi.updatePassengers(token, passengersPayload);
    } else {
      passengersPayload.forEach((p) => Reflect.deleteProperty(p, 'id'));
      payload = await purchaseApi.createPassengers(token, passengersPayload);
    }
  } catch (error) {
    errorMonitoring.notify({
      error,
      info: { from: 'quickPurchase:savePassengers', token },
    });
    throw error;
  }

  const { passengers } = camelizeKeys(payload);
  dispatch(setPassengers(passengers, token));

  try {
    if (features.USE_LEGACY_LOCK_TICKETS) {
      const { departs, returns } = trips;
      const departureSeats = departs[0].selectedSeats?.map(({ number }) => number);
      const lockTicketRequests = [
        dispatch(lockTicketsLegacy('departs', token, departs, passengers, departureSeats)),
      ];

      if (returns) {
        const returnSeats = returns[0].selectedSeats?.map(({ number }) => number);
        lockTicketRequests.push(
          dispatch(lockTicketsLegacy('returns', token, returns, passengers, returnSeats)),
        );
      }

      await Promise.all(lockTicketRequests);
    } else {
      await dispatch(
        lockTickets(token, trips, passengers, { preservePaymentOptionSelection: true }),
      );
    }
  } catch (error) {
    errorMonitoring.notify({
      error,
      info: { from: 'quickPurchase:lockTickets', token },
    });
    throw error;
  }
}

/**
 * saveQuickCheckoutPassengers action creator.
 * Expose savePassengersForQuickPurchase as a dispatchable thunk for use in QuickCheckout.
 * On re-edits the inner helper takes the updatePassengers branch, preserving passenger ids.
 * @param {string} token - Purchase token.
 * @param {object} fields - Passenger and purchaser fields.
 * @param {object} trips - Trips object with departs and optional returns.
 * @returns {Function} Redux thunk.
 */
export function saveQuickCheckoutPassengers(token, fields, trips) {
  return (dispatch) => savePassengersForQuickPurchase(dispatch, token, fields, trips);
}

/**
 * completeQuickPurchase action creator.
 * Complete the quick purchase flow inside QuickCheckout.
 * Fetches trip details, selects seats (user-picked or auto), creates passengers, and locks tickets.
 * Reads frequent passengers directly from Redux store.
 * @param {object} params - passengers, selectedSeatNumbers.
 * @param {string} params.passengers - Encoded passenger string (e.g., "A1", "A2").
 * @param {Array<string>} [params.selectedSeatNumbers] - Seat numbers chosen by the user in seat selector.
 * @returns {Function} Redux thunk that resolves with { success, error }.
 */
export default function completeQuickPurchase({ passengers, selectedSeatNumbers }) {
  return async (dispatch) => {
    try {
      const userPrefs = store.getState().userPreferences.toJS();
      const frequentPassengers = userPrefs.frequentPassengers?.passengers || [];
      const { features } = store.getState().whitelabelConfig;

      const adapted = getQuickPurchasePassengers(frequentPassengers, features);
      const decodedPassengers = decodePassengers(passengers);
      const passengerCount = decodedPassengers.adult || 1;

      const purchaseState = store.getState().purchase.toJS();
      const { token } = purchaseState;

      // 1. Fetch trip details (bus layouts)
      await dispatch(
        fetchTripsDetails({
          departureFragments: purchaseState.departs.fragments,
          returnFragments: purchaseState.returns?.fragments || [],
        }),
      );

      const purchaseAfterDetails = store.getState().purchase.toJS();

      // 2. Select seats for departure: prefer user-picked seats, fallback to auto-select
      const departureLayout = purchaseAfterDetails.departs.layout;
      const departureTripSlug = purchaseAfterDetails.departs.tripSlug;

      let departureSeats = null;
      if (selectedSeatNumbers?.length) {
        departureSeats = findSeatsInLayout(departureLayout, departureTripSlug, selectedSeatNumbers);
      }
      if (!departureSeats) {
        departureSeats = autoSelectSeats(departureLayout, departureTripSlug, passengerCount);
      }

      if (!departureSeats) {
        errorMonitoring.notify({
          error: new Error('NOT_ENOUGH_SEATS_DEPARTURE'),
          info: { from: 'quickPurchase:seatSelection', token, passengerCount },
        });
        return { success: false, error: 'NOT_ENOUGH_SEATS_DEPARTURE', token };
      }

      dispatch(selectSeats('departs', departureSeats));

      // 2b. Auto-select seats for return (if round trip)
      let returnSeats = null;
      if (purchaseAfterDetails.returns?.layout) {
        const returnLayout = purchaseAfterDetails.returns.layout;
        const returnTripSlug = purchaseAfterDetails.returns.tripSlug;
        returnSeats = autoSelectSeats(returnLayout, returnTripSlug, passengerCount);

        if (!returnSeats) {
          errorMonitoring.notify({
            error: new Error('NOT_ENOUGH_SEATS_RETURN'),
            info: { from: 'quickPurchase:seatSelection', token, passengerCount },
          });
          return { success: false, error: 'NOT_ENOUGH_SEATS_RETURN', token };
        }

        dispatch(selectSeats('returns', returnSeats));
      }

      // 3. Build fields for savePassengersWithTickets
      if (!adapted.length) {
        errorMonitoring.notify({
          error: new Error('No passengers available for quick purchase'),
          info: { from: 'quickPurchase:noPassengers', token },
        });
        return { success: false, error: 'NO_PASSENGERS_AVAILABLE' };
      }

      const firstPassenger = adapted[0];
      const quickPassengers = adapted.slice(0, passengerCount);
      const insuranceSelection = getQuickPurchaseInsuranceSelection({
        features,
        purchase: purchaseAfterDetails,
        selected: features.PASSENGER_INSURANCE_BY_DEFAULT,
        passengers: quickPassengers,
      });
      const fields = {
        firstName: firstPassenger.firstName,
        lastName: firstPassenger.lastName,
        secondLastName: firstPassenger.secondLastName || '',
        email: firstPassenger.email,
        phone: firstPassenger.phone,
        phoneCountry: firstPassenger.phoneCountry || 'MX',
        phoneCode: firstPassenger.phoneCode || '',
        documentType: firstPassenger.documentType || '',
        documentId: firstPassenger.documentId || '',
        passengers: quickPassengers.map((fp) => ({
          firstName: fp.firstName || '',
          lastName: fp.lastName || '',
          secondLastName: fp.secondLastName || '',
          email: fp.email || '',
          phone: fp.phone || '',
          phoneCountry: fp.phoneCountry || 'MX',
          phoneCode: fp.phoneCode || '',
          documentType: fp.documentType || '',
          documentId: fp.documentId || '',
          nationality: fp.nationality || '',
          dateOfBirth: fp.dateOfBirth || '',
          gender: fp.gender || '',
          id: '',
          category: 'adult',
          busCategory: 'general',
          isFrequentPassenger: Boolean(fp.isFrequentPassenger),
          preferredPassengerOrder: true,
          ...insuranceSelection,
        })),
      };

      // 4. Build trips object
      const trips = {
        departs: purchaseAfterDetails.departs.fragments.map((frag) => ({
          id: frag.id,
          type: purchaseAfterDetails.departs.details?.transportType || 'bus',
          selectedSeats: departureSeats,
        })),
      };

      if (returnSeats && purchaseAfterDetails.returns?.fragments) {
        trips.returns = purchaseAfterDetails.returns.fragments.map((frag) => ({
          id: frag.id,
          type: purchaseAfterDetails.returns.details?.transportType || 'bus',
          selectedSeats: returnSeats,
        }));
      }

      // 5. Create passengers and lock tickets (awaits lockTickets to prevent race condition)
      await savePassengersForQuickPurchase(dispatch, token, fields, trips);

      return { success: true };
    } catch (error) {
      errorMonitoring.notify({
        error,
        info: {
          from: 'completeQuickPurchase',
          token: store.getState().purchase.toJS().token,
          passengers,
        },
      });
      return { success: false, error: error.message };
    }
  };
}

/**
 * Updates all quick-checkout passengers with the selected passenger-level insurance state.
 * This is the quick-purchase equivalent of the passengers-step insurance controls.
 * @param {string} token - Purchase token.
 * @param {boolean} wants - Whether the user wants passenger insurance.
 * @returns {Function} Redux thunk.
 */
export function updateQuickPurchasePassengerInsurance(token, wants) {
  return async (dispatch) => {
    const {
      whitelabelConfig: { features },
      purchase,
    } = store.getState();
    const purchaseState = purchase.toJS();
    const insuranceSelection = getQuickPurchaseInsuranceSelection({
      features,
      purchase: purchaseState,
      selected: wants,
      passengers: purchaseState.passengers,
    });

    const passengersPayload = decamelizeKeys(
      purchaseState.passengers.map((passenger) => {
        return serializeQuickPurchasePassenger({
          passenger,
          features,
          insuranceSelection,
        });
      }),
    );

    dispatch(toggleInsurance(true));

    try {
      const payload = await purchaseApi.updatePassengers(token, passengersPayload);
      const { passengers } = camelizeKeys(payload);
      dispatch(setPassengers(passengers, token));
      // Refresh purchase totals/tickets while keeping the payment selection the user already
      // chose (see getPurchase preservePaymentOptionSelection) instead of resetting to the
      // tenant default, which would flip the option and remount/wipe the payment form.
      await dispatch(getPurchase(token, true, { preservePaymentOptionSelection: true }));
    } catch (error) {
      dispatch(setError(205, 'error_when_updating_purchase', 'error', false));
      errorMonitoring.notify({
        error,
        info: { from: 'quickPurchase:updatePassengerInsurance', token, wants },
      });
    } finally {
      dispatch(toggleInsurance(false));
    }
  };
}

/**
 * updateQuickPurchaseSeats action creator.
 * Re-locks the departure seats of an in-progress quick purchase WITHOUT re-saving passengers.
 * Reads the current passengers and departure fragments from the store, rebuilds the `trips`
 * object with the new seats and relocks via lockTickets. lockTickets internally refreshes the
 * purchase with preservePaymentOptionSelection, so price/summary update while the payment
 * option and entered card data are preserved. Single-trip only (no returns).
 * @param {string} token - Purchase token.
 * @param {Array<object>} [departureSeats] - Selected seat objects. Falls back to the store.
 * @returns {Function} Redux thunk.
 */
export function updateQuickPurchaseSeats(token, departureSeats) {
  return async (dispatch) => {
    const { features } = store.getState().whitelabelConfig;
    const purchaseState = store.getState().purchase.toJS();
    const { passengers, departs } = purchaseState;
    const seats = departureSeats || departs.selectedSeats;

    // Ensure the store holds the seats we are about to lock (the seat selector already ran
    // selectSeats, this is defensive for callers that pass seats explicitly).
    dispatch(selectSeats('departs', seats));

    const trips = {
      departs: departs.fragments.map((frag) => ({
        id: frag.id,
        type: departs.details?.transportType || 'bus',
        selectedSeats: seats,
      })),
    };

    if (features.USE_LEGACY_LOCK_TICKETS) {
      const seatNumbers = seats?.map(({ number }) => number);
      await dispatch(lockTicketsLegacy('departs', token, trips.departs, passengers, seatNumbers));
      return;
    }

    await dispatch(lockTickets(token, trips, passengers, { preservePaymentOptionSelection: true }));
  };
}
